import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { uniqBy } from 'lodash';

import { FlightsFilterType } from '@apps/main/new-desktop/modules/flightsFilters';
import type {
  ArrivalTimeUpdateFilterEvent,
  CarriersFilterUpdateFilterEvent,
  DepartureTimeUpdateFilterEvent,
  FlightsFilterEvents,
  FlightsFilters,
  NumberOfStopsUpdateFilterEvent,
  PriceFilterUpdateFilterEvent,
  TravelDurationUpdateFilterEvent,
} from '@apps/main/new-desktop/modules/flightsFilters';
import { FlightIngredient } from '@apps/main/modules/recipe/ingredients';

import type {
  CarrierName,
  DecimalRangeType,
  IngredientId,
} from '@libs/trip-planner-interfaces';
import {
  exhaustiveGuard,
  propertyStringComparer,
  getRoundedUpHoursFromMinutes,
} from '@libs/utils';

import { useRecipeStore } from '../recipe/recipeStore';
import { useTripStore } from '../trip/tripStore';
import { useGlobalCurrencyStore } from '../globalCurrencyStore';

import {
  getDefaultFilters,
  getArrivalTimeRangeSliderFilter,
  getDepartureTimeRangeSliderFilter,
  getTravelDurationSliderFilter,
  getPriceSliderFilter,
  getNumberOfStopsFilter,
  getCarriersCheckboxFilter,
  getDecimalRangeType,
  getFlightDurationRangeFromFilters,
  getStringRangeType,
  getFilterKeyFromTagId,
} from './flightsFiltersStoreLogic';
import { FlightFilterType } from './flightFilter.model';

export const useFlightsFiltersStore = defineStore('flightsFiltersStore', () => {
  const tripStore = useTripStore();
  const recipeStore = useRecipeStore();
  const globalCurrencyStore = useGlobalCurrencyStore();

  //Helper getters
  const allowedFlightIngredients = computed<FlightIngredient[]>(() => {
    if (allowedFlightIngredientIds.value?.length) {
      return recipeStore.flightOnlyRecipe.filter((flightIngredient) =>
        allowedFlightIngredientIds.value?.includes(
          flightIngredient.ingredientId
        )
      );
    }
    return recipeStore.flightOnlyRecipe;
  });

  //State
  const flightsDurationRange = ref<Record<string, DecimalRangeType>>({});
  const allowedFlightIngredientIds = ref<string[] | null>(null);

  //Getters
  const filters = computed<FlightsFilters | null>(() => {
    if (recipeStore.flightOnlyRecipe?.length === 0) {
      return null;
    }

    const newFilters: FlightsFilters = getDefaultFilters();

    allowedFlightIngredients.value.forEach((flight) => {
      newFilters.ingredientId = flight.ingredientId;
      newFilters.arrivalTime.filters.push(
        getArrivalTimeRangeSliderFilter(flight)
      );

      newFilters.departureTime.filters.push(
        getDepartureTimeRangeSliderFilter(flight)
      );

      const currentFlightRange =
        flightsDurationRange.value[flight.ingredientId];

      const durationRangeFromFlight =
        currentFlightRange ??
        tripStore.getDurationRangeInMinutesFromItineraries(flight.ingredientId);

      const durationFilters = getTravelDurationSliderFilter(
        flight,
        durationRangeFromFlight?.from,
        durationRangeFromFlight?.to
      );

      newFilters.travelDuration.filters.push(durationFilters);
      newFilters.price.filters.push(
        getPriceSliderFilter(flight, globalCurrencyStore.globalCurrency)
      );
      newFilters.numberOfStops.filters.push(getNumberOfStopsFilter(flight));

      const flightCarriers = tripStore.getFlightAllCarriers(
        flight.ingredientId
      ).allCarriers;

      const uniqueCarriers = uniqBy(flightCarriers, 'name').sort(
        propertyStringComparer((carrier: CarrierName) => carrier.name)
      );
      newFilters.carriers.data.push({
        title: `${flight.fromPortText} - ${flight.toPortText}`,
        filters: getCarriersCheckboxFilter(uniqueCarriers, flight),
        ingredientId: flight.ingredientId,
      });
    });
    return newFilters;
  });

  //Actions
  function removeFilter(tagId: string, ingredientId: IngredientId): void {
    const flightIngredient = recipeStore.getFlightIngredient(ingredientId);
    const filterKey = getFilterKeyFromTagId(tagId);

    if (filterKey === FlightFilterType.CarrierFilters) {
      const carrierCode = tagId.split('.')[1];

      if (carrierCode) {
        // HACK: The only way I found to update carrier filters
        flightIngredient.filters.carrierFilters = [
          ...flightIngredient.filters.carrierFilters,
        ];

        const carrierCodeIndex =
          flightIngredient.filters.carrierFilters.findIndex(
            (code) => code === carrierCode
          );

        flightIngredient.filters.carrierFilters.splice(carrierCodeIndex, 1);
      }
    } else {
      flightIngredient.filters[tagId] = FlightIngredient.defaultFilters[tagId];
    }
  }

  function resetFlightFilters(ingredientId: IngredientId): void {
    const flightIngredient = recipeStore.getFlightIngredient(ingredientId);
    flightIngredient.filters = FlightIngredient.defaultFilters;
  }

  function setAllowedFlightIngredientIds(ingredientIds: string[]): void {
    allowedFlightIngredientIds.value = ingredientIds;
  }

  function clearAllowedFlightIngredientIds(): void {
    allowedFlightIngredientIds.value = null;
  }

  function updateAllFilters(actionEvents: FlightsFilterEvents[]): void {
    actionEvents.forEach(update);
  }

  function update(action: FlightsFilterEvents): void {
    switch (action.filterType) {
      case FlightsFilterType.ArrivalTime: {
        onUpdateArrivalTimeFilter(action);
        break;
      }
      case FlightsFilterType.Carriers:
        onUpdateCarriersFilter(action);
        break;
      case FlightsFilterType.DepartureTime: {
        onUpdateDepartureFilter(action);
        break;
      }
      case FlightsFilterType.NumberOfStops:
        onUpdateStopsFilter(action);
        break;
      case FlightsFilterType.Price:
        onUpdatePriceFilter(action);
        break;
      case FlightsFilterType.TravelDuration:
        onUpdateDurationFilter(action);
        break;
      default:
        exhaustiveGuard(action, `filterType not supported`);
    }
  }

  // Private helpers
  function onUpdateArrivalTimeFilter(
    action: ArrivalTimeUpdateFilterEvent
  ): void {
    const ingredient = recipeStore.getFlightIngredient(
      action.data.ingredientId
    );
    ingredient.filters.arrivalTimeFilters = getStringRangeType(
      action.data.range
    );
  }

  function onUpdateCarriersFilter(
    action: CarriersFilterUpdateFilterEvent
  ): void {
    const selectedCarrier = action.data.selected;
    const flightIngredient = recipeStore.getFlightIngredient(
      action.data.ingredientId
    );

    // HACK: The only way I found to update carrier filters
    flightIngredient.filters.carrierFilters = [
      ...flightIngredient.filters.carrierFilters,
    ];
    const carrierIndex =
      flightIngredient.filters.carrierFilters.indexOf(selectedCarrier);
    if (carrierIndex > -1) {
      flightIngredient.filters.carrierFilters.splice(carrierIndex, 1);
    } else {
      flightIngredient.filters.carrierFilters.push(selectedCarrier);
    }
  }

  function onUpdateDepartureFilter(
    action: DepartureTimeUpdateFilterEvent
  ): void {
    const ingredient = recipeStore.getFlightIngredient(
      action.data.ingredientId
    );
    ingredient.filters.departureTimeFilters = getStringRangeType(
      action.data.range
    );
  }

  function onUpdateStopsFilter(action: NumberOfStopsUpdateFilterEvent): void {
    const maxNumberOfStops = action.data.selected;

    const ingredient = recipeStore.getFlightIngredient(
      action.data.ingredientId
    );
    ingredient.filters.maxNumberOfStops = maxNumberOfStops;
  }

  function onUpdatePriceFilter(action: PriceFilterUpdateFilterEvent): void {
    const ingredient = recipeStore.getFlightIngredient(
      action.data.ingredientId
    );
    ingredient.filters.priceFilters = getDecimalRangeType(action.data.value);
  }

  function onUpdateDurationFilter(
    action: TravelDurationUpdateFilterEvent
  ): void {
    const maxDurationRange = tripStore.getDurationRangeInMinutesFromItineraries(
      action.data.ingredientId
    );
    const ingredient = recipeStore.getFlightIngredient(
      action.data.ingredientId
    );

    const currentFlightRange =
      flightsDurationRange.value[ingredient.ingredientId];

    if (!currentFlightRange) {
      const flightDurationRange = getFlightDurationRangeFromFilters(
        ingredient.ingredientId,
        filters.value
      );

      if (flightDurationRange) {
        flightsDurationRange.value[ingredient.ingredientId] =
          flightDurationRange;
      }
    }

    ingredient.filters.maxDurationInHours =
      maxDurationRange?.to === action.data.value
        ? undefined
        : getRoundedUpHoursFromMinutes(action.data.value);
  }

  return {
    updateAllFilters,
    filters,
    update,
    setAllowedFlightIngredientIds,
    clearAllowedFlightIngredientIds,
    resetFlightFilters,
    removeFilter,
  };
});
